/**
 * Task 36: My Follow-Ups Page
 * Displays categorized follow-ups: Overdue, Today, Upcoming, Completed
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, User, FileText, Loader2 } from 'lucide-react';
import axiosInstance from '../config/axiosConfig';
import toast from '../utils/toast';
import eventBus from '../utils/eventBus';
import { useNavigate } from 'react-router-dom';

const MyFollowUps = () => {
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState({
    overdue: [],
    today: [],
    upcoming: [],
    completed: []
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadFollowUps();
  }, []);

  const loadFollowUps = async () => {
    try {
      const response = await axiosInstance.get('/followups/my');
      setFollowups(response.data);
    } catch (error) {
      console.error('Failed to load follow-ups:', error);
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const markAsCompleted = async (followupId) => {
    try {
      await axiosInstance.post(`/followups/${followupId}/complete`);
      
      // Emit analytics event
      try {
        eventBus.emit('followup_completed', {
          followup_id: followupId,
          channel: 'desktop',
          source: 'MyFollowUps',
          timestamp: new Date().toISOString()
        });
      } catch (eventBusError) {
        console.warn('EventBus failed to emit followup_completed event:', eventBusError);
      }
      
      toast.success('Follow-up marked as completed!');
      loadFollowUps(); // Reload data
    } catch (error) {
      console.error('Failed to complete follow-up:', error);
      toast.error('Failed to mark as completed');
    }
  };

  const getFollowUpIcon = (type) => {
    const icons = {
      call: '📞',
      whatsapp: '💬',
      email: '📧',
      document: '📄',
      interview: '🎯',
      other: '📌'
    };
    return icons[type] || '📌';
  };

  const FollowUpCard = ({ followup, category }) => {
    const isCompleted = category === 'completed';
    const isPending = !isCompleted;

    return (
      <div 
        className={`p-4 rounded-lg border-l-4 ${
          category === 'overdue' 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-500' 
            : category === 'today'
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
            : category === 'upcoming'
            ? 'bg-gray-50 dark:bg-gray-800 border-gray-400'
            : 'bg-green-50 dark:bg-green-900/20 border-green-500'
        } hover:shadow-md transition cursor-pointer`}
        onClick={() => navigate(`/candidates/${followup.candidate_id}`)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Candidate Name */}
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {followup.candidate_name}
              </h3>
            </div>
            
            {/* Follow-up Type */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{getFollowUpIcon(followup.followup_type)}</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                {followup.followup_type}
              </span>
            </div>
            
            {/* Notes */}
            {followup.notes && (
              <div className="flex items-start gap-2 mb-2">
                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {followup.notes}
                </p>
              </div>
            )}
            
            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>
                {isCompleted ? 'Completed' : 'Target'}: {followup.target_date}
              </span>
            </div>
          </div>
          
          {/* Action Button */}
          {isPending && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                markAsCompleted(followup.id);
              }}
              className="px-3 py-1.5 bg-teal hover:bg-teal-dark text-white text-sm rounded-lg transition flex items-center gap-1 flex-shrink-0"
            >
              <CheckCircle className="w-4 h-4" />
              Done
            </button>
          )}
        </div>
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title, count, color }) => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-white">
          {title}
        </h2>
      </div>
      <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium">
        {count}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  const totalPending = followups.overdue.length + followups.today.length + followups.upcoming.length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-navy-darker p-6">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-2">
            My Follow-Ups
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {totalPending} pending • {followups.completed.length} completed
          </p>
        </div>

        <div className="space-y-8">
          {/* Overdue */}
          {followups.overdue.length > 0 && (
            <div>
              <SectionHeader 
                icon={AlertCircle} 
                title="Overdue" 
                count={followups.overdue.length}
                color="bg-red-500"
              />
              <div className="space-y-3">
                {followups.overdue.map(followup => (
                  <FollowUpCard key={followup.id} followup={followup} category="overdue" />
                ))}
              </div>
            </div>
          )}

          {/* Today */}
          {followups.today.length > 0 && (
            <div>
              <SectionHeader 
                icon={Clock} 
                title="Today" 
                count={followups.today.length}
                color="bg-blue-500"
              />
              <div className="space-y-3">
                {followups.today.map(followup => (
                  <FollowUpCard key={followup.id} followup={followup} category="today" />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming */}
          {followups.upcoming.length > 0 && (
            <div>
              <SectionHeader 
                icon={Calendar} 
                title="Upcoming (Next 7 Days)" 
                count={followups.upcoming.length}
                color="bg-gray-500"
              />
              <div className="space-y-3">
                {followups.upcoming.map(followup => (
                  <FollowUpCard key={followup.id} followup={followup} category="upcoming" />
                ))}
              </div>
            </div>
          )}

          {/* Completed */}
          {followups.completed.length > 0 && (
            <div>
              <SectionHeader 
                icon={CheckCircle} 
                title="Completed (Last 7 Days)" 
                count={followups.completed.length}
                color="bg-green-500"
              />
              <div className="space-y-3">
                {followups.completed.map(followup => (
                  <FollowUpCard key={followup.id} followup={followup} category="completed" />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {totalPending === 0 && followups.completed.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No follow-ups yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Add follow-ups from candidate pages to stay organized
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyFollowUps;
